// ============================================================================
// Glowp Analytics — Plausible Custom Events (config maximale)
// ============================================================================

declare global {
  interface Window {
    plausible: (
      event: string,
      options?: {
        props?: Record<string, string | number | boolean>;
        revenue?: { currency: string; amount: number };
      }
    ) => void;
  }
}

function track(
  event: string,
  props?: Record<string, string | number | boolean>,
  revenue?: { currency: string; amount: number }
) {
  if (typeof window !== 'undefined' && window.plausible) {
    const options: Record<string, unknown> = {};
    if (props) options.props = props;
    if (revenue) options.revenue = revenue;
    window.plausible(
      event,
      Object.keys(options).length > 0 ? options : undefined
    );
  }
}

// ============================================================================
// Helper — user connecte ou pas
// ============================================================================

function isLoggedIn(): boolean {
  return !!localStorage.getItem('glowp_access_token');
}

// ============================================================================
// 1. RETENTION — first_seen + returning_visit
// ============================================================================

const FIRST_SEEN_KEY = 'glowp_first_seen';
const LAST_VISIT_KEY = 'glowp_last_visit';

function initRetention() {
  if (typeof window === 'undefined') return;

  const now = Date.now();
  const firstSeen = localStorage.getItem(FIRST_SEEN_KEY);
  const lastVisit = localStorage.getItem(LAST_VISIT_KEY);
  const logged_in = isLoggedIn();

  if (!firstSeen) {
    // Tout premier visit
    localStorage.setItem(FIRST_SEEN_KEY, String(now));
    track('new_visitor', { landing_page: window.location.pathname, logged_in });
  } else {
    // Visitor qui revient
    const firstSeenMs = parseInt(firstSeen, 10);
    const daysSinceFirst = Math.floor((now - firstSeenMs) / 86_400_000);
    const lastVisitMs = lastVisit ? parseInt(lastVisit, 10) : 0;
    const daysSinceLastVisit = lastVisitMs
      ? Math.floor((now - lastVisitMs) / 86_400_000)
      : 0;

    // Ne tracker qu'une fois par session (tab ferme = nouvelle session)
    if (daysSinceLastVisit >= 1) {
      track('returning_visit', {
        days_since_first: daysSinceFirst,
        days_since_last: daysSinceLastVisit,
        logged_in,
      });
    }
  }

  localStorage.setItem(LAST_VISIT_KEY, String(now));
}

// ============================================================================
// 2. ENGAGEMENT — page engagée si user reste > 10s
// ============================================================================

function initEngagement() {
  if (typeof window === 'undefined') return;

  let engaged = false;
  const page = window.location.pathname;

  // Timer: user reste 10s = engagé (pas un bounce)
  const timer = setTimeout(() => {
    if (!engaged) {
      engaged = true;
      track('engaged_visit', { page, logged_in: isLoggedIn() });
    }
  }, 10_000);

  // Scroll: user scroll > 50% = engage
  const handleScroll = () => {
    if (engaged) return;
    const scrollPercent = Math.round(
      (window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100
    );
    if (scrollPercent >= 50) {
      engaged = true;
      track('engaged_visit', { page, logged_in: isLoggedIn() });
      window.removeEventListener('scroll', handleScroll);
    }
  };

  window.addEventListener('scroll', handleScroll, { passive: true });

  // Cleanup si SPA navigation (ne sera pas appelé systématiquement, c'est OK)
  return () => {
    clearTimeout(timer);
    window.removeEventListener('scroll', handleScroll);
  };
}

// ============================================================================
// Init automatique au chargement
// ============================================================================

if (typeof window !== 'undefined') {
  // Attendre que le DOM soit prêt
  if (document.readyState === 'complete') {
    initRetention();
    initEngagement();
  } else {
    window.addEventListener(
      'load',
      () => {
        initRetention();
        initEngagement();
      },
      { once: true }
    );
  }
}

// ============================================================================
// Events exportés
// ============================================================================

export const analytics = {
  // ── Auth ──────────────────────────────────────────────────────────────────
  userLoggedIn: (method: 'email' | 'google' | 'apple' = 'email') =>
    track('user_logged_in', { method }),
  userRegistered: (method: 'email' | 'google' | 'apple' = 'email') =>
    track('user_registered', { method }),
  userLoggedOut: () => track('user_logged_out'),

  loginFromModal: () => track('login_from_modal'),
  registerFromModal: () => track('register_from_modal'),
  authModalToggled: (to: 'login' | 'register') =>
    track('auth_modal_toggled', { to }),

  // ── Registration Funnel ─────────────────────────────────────────────────
  registrationModalOpened: (source: string) =>
    track('registration_modal_opened', { source }),
  registrationFieldFilled: (field: string) =>
    track('registration_field_filled', { field }),
  registrationFieldError: (field: string, error: string) =>
    track('registration_field_error', { field, error }),
  registrationSubmitted: () => track('registration_submitted'),
  registrationFailed: (reason: string) =>
    track('registration_failed', { reason: reason.slice(0, 200) }),
  registrationAbandoned: (lastField: string, filledCount: number) =>
    track('registration_abandoned', { lastField, filledCount }),
  registrationModalClosedNoAction: () =>
    track('registration_modal_closed_no_action'),

  // ── Email Verification Funnel ────────────────────────────────────────────
  // Mesure : taux verify, temps verify, recovery usage (resend / change /
  // provider opens). Permet de detecter les points de friction du funnel
  // d activation email (cf. drop observe en mai 2026 apres l ecran zero-
  // friction qui cachait completement l email a verifier).
  signupCompletedScreenShown: () => track('signup_completed_screen_shown'),
  emailProviderOpened: (provider: string) =>
    track('email_provider_opened', { provider }),
  emailResendClicked: (source: 'post_signup' | 'login_blocked') =>
    track('email_resend_clicked', { source }),
  emailResendResult: (
    source: 'post_signup' | 'login_blocked',
    result: 'success' | 'error'
  ) => track('email_resend_result', { source, result }),
  emailChangeOpened: () => track('email_change_opened'),
  emailChangeSubmitted: (
    result: 'success' | 'error_invalid' | 'error_conflict' | 'error_server'
  ) => track('email_change_submitted', { result }),
  emailVerifiedViaPolling: () => track('email_verified_via_polling'),
  emailVerifiedViaPage: (status: 'success' | 'expired' | 'error') =>
    track('email_verified_via_page', { status }),

  // ── Catalog (Buyer Funnel) ───────────────────────────────────────────────
  // Fire quand l'utilisateur ouvre une surface de browse (catalogue, search…)
  // source = entry-point ('direct', 'home_cta', 'nav', 'search_submit'…)
  catalogOpened: (source: string, audience?: string) =>
    track('catalog_opened', {
      source,
      ...(audience && { audience }),
      logged_in: isLoggedIn(),
    }),

  // Fire UNE FOIS quand une section discovery entre dans le viewport (>= 20%
  // visible) avec du contenu réel. Permet de calculer la vraie CTR par
  // surface = product_card_clicked / discovery_section_shown. Sans ça, on
  // mesure les clics absolus, pas le ratio qui compare les surfaces.
  discoverySectionShown: (surface: string, cardsCount: number) =>
    track('discovery_section_shown', {
      surface,
      cardsCount,
      logged_in: isLoggedIn(),
    }),

  // Fire quand une carte produit est cliquée sur N'IMPORTE QUELLE surface.
  // surface (obligatoire) = zone où la carte vit. Enum recommandé :
  //   home_latest_arrivals | home_curated | products_grid | recently_viewed
  //   for_you | creator_picks | new_in_style | search_results | shop_page
  //   community_feed | favorites | recommendations | similar_products
  // position (optionnel) = index dans la liste, pour savoir si haut/bas du feed
  // extra (optionnel) = contexte additionnel (brand, condition, boost_level…)
  productCardClicked: (
    productId: string,
    surface: string,
    position?: number,
    extra?: Record<string, string | number | boolean>
  ) =>
    track('product_card_clicked', {
      productId,
      surface,
      ...(position !== undefined && { position }),
      ...(extra || {}),
      logged_in: isLoggedIn(),
    }),

  // ── Product ──────────────────────────────────────────────────────────────
  productViewed: (productId: string, category?: string) =>
    track('product_viewed', { productId, ...(category && { category }) }),

  productShared: (productId: string, platform: string) =>
    track('product_shared', { productId, platform }),

  productFavorited: (productId: string) =>
    track('product_favorited', { productId }),

  productUnfavorited: (productId: string) =>
    track('product_unfavorited', { productId }),

  favoriteRemoved: (productId: string) =>
    track('favorite_removed', { productId }),

  // ── Cart ──────────────────────────────────────────────────────────────────
  addToCart: (productId: string) => track('add_to_cart', { productId }),

  removeFromCart: (itemId: string) => track('remove_from_cart', { itemId }),

  cartUpdated: (itemId: string, quantity: number) =>
    track('cart_updated', { itemId, quantity }),

  buyNowClicked: (productId: string) => track('buy_now_clicked', { productId }),

  // ── Checkout ─────────────────────────────────────────────────────────────
  checkoutStarted: (itemCount: number, total: number) =>
    track(
      'checkout_started',
      { itemCount, total },
      { currency: 'EUR', amount: total }
    ),

  deliveryMethodSelected: (type: string) =>
    track('delivery_method_selected', { type }),

  paymentSubmitted: (method: string, total: number) =>
    track(
      'payment_submitted',
      { method, total },
      { currency: 'EUR', amount: total }
    ),

  // ── Purchase (revenue tracking) ──────────────────────────────────────────
  purchaseCompleted: (orderId: string, total: number, itemCount: number) =>
    track(
      'purchase_completed',
      { orderId, itemCount },
      { currency: 'EUR', amount: total }
    ),

  // ── Seller Listing Funnel ────────────────────────────────────────────────
  listingStarted: () => track('listing_started'),
  listingPhotosAdded: (count: number) =>
    track('listing_photos_added', { count }),
  listingDetailsFilled: (hasTitle: boolean, hasPrice: boolean) =>
    track('listing_details_filled', { hasTitle, hasPrice }),
  // listingPublished — etendu pour mesurer l'impact du picker "Pour qui ?"
  // (livre 2026-05-23). targetGender = choix final ; targetGenderPrefill =
  // ce que le picker avait pre-coche (heritage du profil categorie) ;
  // pickerShown = est-ce que la categorie a declenche le picker. La
  // difference prefill vs final mesure le "reflexe Femme" (si > 50% des
  // vendeuses laissent par defaut, le picker n'est qu'un placebo).
  // timeMs = duree creation depuis listingStarted (signal de friction).
  listingPublished: (
    price: number,
    photoCount: number,
    hasDefects: boolean,
    extras?: {
      targetGender?: string | null;
      targetGenderPrefill?: string | null;
      pickerShown?: boolean;
      timeMs?: number;
    }
  ) =>
    track('listing_published', {
      price,
      photoCount,
      hasDefects,
      ...(extras?.targetGender !== undefined
        ? { targetGender: extras.targetGender ?? 'NULL' }
        : {}),
      ...(extras?.targetGenderPrefill !== undefined
        ? { targetGenderPrefill: extras.targetGenderPrefill ?? 'NULL' }
        : {}),
      ...(extras?.pickerShown !== undefined
        ? { pickerShown: extras.pickerShown }
        : {}),
      ...(typeof extras?.timeMs === 'number'
        ? { timeMs: Math.round(extras.timeMs) }
        : {}),
    }),
  // listingAbandoned — etendu avec pickerShown pour distinguer un abandon
  // sur une categorie ou le picker est apparu (friction potentielle) d'un
  // abandon "ordinaire". timeMs = combien de temps avant d'abandonner.
  listingAbandoned: (
    step: string,
    photosCount: number,
    hadTitle: boolean,
    extras?: { pickerShown?: boolean; timeMs?: number }
  ) =>
    track('listing_abandoned', {
      step,
      photosCount,
      hadTitle,
      ...(extras?.pickerShown !== undefined
        ? { pickerShown: extras.pickerShown }
        : {}),
      ...(typeof extras?.timeMs === 'number'
        ? { timeMs: Math.round(extras.timeMs) }
        : {}),
    }),

  // ── Search ───────────────────────────────────────────────────────────────
  searchPerformed: (query: string, resultsCount: number) =>
    track('search_performed', { query: query.slice(0, 100), resultsCount }),

  searchResultClicked: (productId: string, query: string) =>
    track('search_result_clicked', { productId, query: query.slice(0, 100) }),

  headerSearchSubmitted: (query: string) =>
    track('header_search_submitted', { query: query.slice(0, 100) }),

  heroSearchSubmitted: (query: string) =>
    track('hero_search_submitted', { query: query.slice(0, 100) }),

  searchSortChanged: (sortBy: string) =>
    track('search_sort_changed', { sortBy }),

  searchFilterApplied: (filterType: string, value: string) =>
    track('search_filter_applied', { filterType, value }),

  searchNoResults: (query: string) =>
    track('search_no_results', { query: query.slice(0, 100) }),

  // ── Shopping Mission ────────────────────────────────────────────────────
  // Capture l'entonnoir complet : entree dans le wizard, abandon a chaque
  // step, mission creee, qualite des resultats, engagement sur les
  // DressingCards. Permet d'ajuster scoring/UX sans deviner.
  missionButtonClicked: (
    source: 'header_desktop' | 'header_mobile' | 'home_chip'
  ) => track('mission_button_clicked', { source }),

  missionWizardStep: (step: number, hasProfile: boolean) =>
    track('mission_wizard_step', { step, hasProfile }),

  missionWizardAbandoned: (lastStep: number, itemsAdded: number) =>
    track('mission_wizard_abandoned', { lastStep, itemsAdded }),

  missionCreated: (params: {
    itemsCount: number;
    distinctCategories: number;
    hasProfile: boolean;
    hasBudget: boolean;
    totalQuantity: number;
  }) => track('mission_created', params),

  missionResultsLoaded: (params: {
    matchCount: number;
    isEmpty: boolean;
    topScore: number | null;
  }) => track('mission_results_loaded', params),

  missionDressingClicked: (params: {
    shopId: string | null;
    coveredItems: number;
    inBudget: boolean;
    position: number;
  }) => track('mission_dressing_clicked', params),

  // ── Catalogue Filters ────────────────────────────────────────────────────
  filterAudienceSelected: (audience: string) =>
    track('filter_audience_selected', { audience }),

  filterPanelOpened: (filter: string) =>
    track('filter_panel_opened', { filter }),

  filterCategoryToggled: (category: string) =>
    track('filter_category_toggled', { category }),

  filtersCleared: () => track('filters_cleared'),

  profileFiltersToggled: (enabled: boolean) =>
    track('profile_filters_toggled', { enabled }),

  // ── Recommendations ──────────────────────────────────────────────────────
  recommendationFeedback: (
    productId: string,
    type: 'like' | 'dislike',
    reason?: string
  ) =>
    track('recommendation_feedback', {
      productId,
      type,
      ...(reason && { reason }),
    }),

  // ── Seller Onboarding ────────────────────────────────────────────────────
  sellerOnboardingOpened: () => track('seller_onboarding_opened'),
  sellerOnboardingStep1Completed: () =>
    track('seller_onboarding_step1_completed'),
  sellerShopCreated: (shopId: string) =>
    track('seller_shop_created', { shopId }),

  // ── Seller Dashboard ─────────────────────────────────────────────────────
  sellerCreateListingClicked: () => track('seller_create_listing_clicked'),
  sellerProductEditClicked: (productId: string) =>
    track('seller_product_edit_clicked', { productId }),
  sellerAddPhotosOpened: (productId: string) =>
    track('seller_add_photos_opened', { productId }),
  sellerPhotosUploaded: (count: number) =>
    track('seller_photos_uploaded', { count }),
  sellerPriceUpdated: (productId: string) =>
    track('seller_price_updated', { productId }),
  dashboardPeriodChanged: (period: string) =>
    track('dashboard_period_changed', { period }),

  // ── Subscription ─────────────────────────────────────────────────────────
  subscriptionUpgradeClicked: (plan: string, billing: string) =>
    track('subscription_upgrade_clicked', { plan, billing }),
  subscriptionBillingToggled: (billing: string) =>
    track('subscription_billing_toggled', { billing }),

  // ── Messaging ────────────────────────────────────────────────────────────
  conversationStarted: (productId: string) =>
    track('conversation_started', { productId }),
  conversationOpened: () => track('conversation_opened'),
  messageSent: () => track('message_sent'),
  lotOfferSent: (itemCount: number, proposedPrice: number) =>
    track(
      'lot_offer_sent',
      { itemCount, proposedPrice },
      { currency: 'EUR', amount: proposedPrice }
    ),
  offerResponded: (status: 'ACCEPTED' | 'REJECTED') =>
    track('offer_responded', { status }),

  // ── Navigation ───────────────────────────────────────────────────────────
  navTabClicked: (path: string) => track('nav_tab_clicked', { path }),
  languageChanged: (locale: string) => track('language_changed', { locale }),

  // ── Photo Upload ─────────────────────────────────────────────────────────
  photoUploaded: (count: number) => track('photo_uploaded', { count }),
  photoCapturedCamera: () => track('photo_captured_camera'),
  videoRecorded: () => track('video_recorded'),

  // ── Errors ───────────────────────────────────────────────────────────────
  paymentError: (error: string) =>
    track('payment_error', { error: error.slice(0, 200) }),
  apiError: (endpoint: string, status: number) =>
    track('api_error', { endpoint, status }),

  // ── 404 ──────────────────────────────────────────────────────────────────
  pageNotFound: (path: string) => track('404', { path }),

  // ── Engagement ───────────────────────────────────────────────────────────
  scrollDepth: (page: string, depth: number) =>
    track('scroll_depth', { page, depth }),

  ctaClicked: (name: string, location: string) =>
    track('cta_clicked', { name, location }),

  sectionSeen: (page: string, section: string) =>
    track('section_seen', { page, section }),

  // ── Page Time ───────────────────────────────────────────────────────────
  pageTimeSpent: (page: string, seconds: number) =>
    track('page_time_spent', { page, seconds, logged_in: isLoggedIn() }),
} as const;
